class AccordionToggle extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.content = null;
    this.storageKey = this.dataset.storageKey || null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('[data-accordion-trigger]');
    this.content = this.querySelector('[data-accordion-content]');

    if (!this.trigger || !this.content) {
      console.warn('AccordionToggle: Missing required elements (trigger or content)');
      return;
    }

    this.setupAccessibility();
    this.attachEvents();
    this.restoreState();
  }

  setupAccessibility() {
    const contentId = this.content.id || `accordion-${Math.random().toString(36).substr(2, 9)}`;
    this.content.id = contentId;

    this.trigger.setAttribute('aria-controls', contentId);

    const isExpanded = this.hasAttribute('data-expanded');
    this.trigger.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

    if (!isExpanded) {
      this.content.removeAttribute('data-expanded');
    }
  }

  attachEvents() {
    this.trigger.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const isExpanded = this.trigger.getAttribute('aria-expanded') === 'true';
    isExpanded ? this.close() : this.open();
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.content.setAttribute('data-expanded', 'true');
    this.saveState(true);

    const group = this.dataset.accordionGroup;
    if (group) {
      document.querySelectorAll(`accordion-toggle[data-accordion-group="${group}"]`).forEach(sibling => {
        if (sibling !== this && sibling.trigger?.getAttribute('aria-expanded') === 'true') {
          sibling.close();
        }
      });
    }

    this.publishEvent('open');

    const focusTarget = this.content.querySelector(this.dataset.focusTarget || 'input, button, [tabindex="0"]');
    if (focusTarget) {
      requestAnimationFrame(() => {
        focusTarget.focus();
      });
    }
  }

  close() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.content.removeAttribute('data-expanded');
    this.saveState(false);

    this.publishEvent('close');
  }

  publishEvent(action) {
    if (typeof theme !== 'undefined' && theme.pubsub) {
      const eventName = this.dataset.event || 'accordion-toggle';
      theme.pubsub.publish(`${eventName}:${action}`, {
        element: this,
        isExpanded: action === 'open'
      });
    }

    this.dispatchEvent(new CustomEvent('accordion:toggle', {
      detail: {
        action,
        isExpanded: action === 'open'
      },
      bubbles: true
    }));
  }

  saveState(isExpanded) {
    if (!this.storageKey) return;

    try {
      sessionStorage.setItem(this.storageKey, isExpanded ? 'true' : 'false');
    } catch (error) {
      console.error('AccordionToggle: Error saving state:', error);
    }
  }

  restoreState() {
    if (!this.storageKey) return;

    try {
      const savedState = sessionStorage.getItem(this.storageKey);

      if (savedState === 'true') {
        this.open();
      } else if (savedState === 'false') {
        this.close();
      }
    } catch (error) {
      console.error('AccordionToggle: Error restoring state:', error);
    }
  }
}

customElements.define('accordion-toggle', AccordionToggle);
